import React, { FC, useMemo } from 'react';
import Checkbox from '@minecraft.checkbox';
import { BodyTextLarge, BodyTextSmall, FlexCol, FlexRow, GridDynamic, H2Text } from '@minecraft.atoms';
import { RadioInput } from '@minecraft.molecules';
import CloudinaryVideoAudio from '@minecraft.cloudinary-video-audio';
import CloudinaryPhoto from '@minecraft.cloudinary-photo';
import { useTranslation, TranslateFn } from '@blocs.i18n';
import { MediaTypeCodeEnum, BaseMediaFragment } from '@minecraft.graphql-operations';

type StringUses = 'title' | 'label' | 'empty';
type Values = MediaTypeCodeEnum.Audio | MediaTypeCodeEnum.Video | MediaTypeCodeEnum.Photo | 'multiple';

type TranslatedMediaStrings = { [K in StringUses]: { [key in Values]: string } };

export const mediaStrings = (t: TranslateFn): TranslatedMediaStrings => ({
  title: {
    [MediaTypeCodeEnum.Audio]: t('common:mediaHelper.audio.add'),
    [MediaTypeCodeEnum.Video]: t('common:mediaHelper.video.add'),
    [MediaTypeCodeEnum.Photo]: t('common:mediaHelper.photo.addPlural'),
    multiple: t('common:profile.mediaBank.addMedia'),
  },
  label: {
    [MediaTypeCodeEnum.Audio]: t('common:mediaHelper.audio.add'),
    [MediaTypeCodeEnum.Video]: t('common:mediaHelper.video.add'),
    [MediaTypeCodeEnum.Photo]: t('common:mediaHelper.photo.addThis'),
    multiple: '',
  },
  empty: {
    [MediaTypeCodeEnum.Audio]: t('common:mediaHelper.audio.noFiles'),
    [MediaTypeCodeEnum.Video]: t('common:mediaHelper.video.noFiles'),
    [MediaTypeCodeEnum.Photo]: t('common:mediaHelper.photo.noFiles'),
    multiple: t('common:profile.mediaBank.noMediaAvailable'),
  },
});

type MediaBinBaseMedia = {
  mediaId: string | number;
  isLocked?: boolean;
  thumbnailUrl?: string;
  mediaStatus: { code: string };
  mediaStorageStatus: { code: string };
  fileKey?: string;
  thumbnail?: { url: string; fileKey: string; transformation?: BaseMediaFragment['transformation'] };
  mediaType?: { code: string };
  name?: string;
  transformation?: BaseMediaFragment['transformation'];
};

export type MediaBinProps = {
  'data-qa-id'?: string;
  mediaTypes: MediaTypeCodeEnum[];
  medias: MediaBinBaseMedia[];
  selectedMediaIds: (string | number)[];
  isSingleSelection: boolean;
  onChange?: (media: MediaBinBaseMedia) => void;
  maxSelections?: number;
  selectAllHandler?: (checked: boolean) => void;
};

interface MediaRendererProps {
  media: MediaBinBaseMedia;
}

const MediaRenderer = ({ media }: MediaRendererProps) => {
  if (!media) {
    return null;
  }

  const {
    thumbnailUrl,
    mediaStatus: { code: mediaStatusCode },
    mediaStorageStatus,
    fileKey,
    thumbnail,
    mediaType: { code: mediaTypeCode },
    name,
    transformation,
  } = media;

  if (mediaTypeCode === MediaTypeCodeEnum.Audio) {
    return (
      <FlexCol w="full" wrap="nowrap" alignItems="center" justifyContent="center">
        <CloudinaryVideoAudio
          mediaType={MediaTypeCodeEnum.Audio}
          mediaStatus={mediaStatusCode}
          fileKey={fileKey}
          mediaStorageStatus={mediaStorageStatus.code}
          thumbnailUrl={thumbnailUrl}
          thumbnail={thumbnail}
          type="sm"
        />
        <BodyTextSmall className="cn_word_break-break">{name}</BodyTextSmall>
      </FlexCol>
    );
  }

  if (mediaTypeCode === MediaTypeCodeEnum.Video) {
    return (
      <FlexCol w="full" wrap="nowrap" alignItems="center" justifyContent="center">
        <CloudinaryVideoAudio
          mediaType={MediaTypeCodeEnum.Video}
          mediaStatus={mediaStatusCode}
          fileKey={fileKey}
          mediaStorageStatus={mediaStorageStatus.code}
          thumbnailUrl={thumbnailUrl}
          thumbnail={thumbnail}
          type="sm"
        />
        <BodyTextSmall className="cn_word_break-break">{name}</BodyTextSmall>
      </FlexCol>
    );
  }

  if (mediaTypeCode === MediaTypeCodeEnum.Photo) {
    return (
      <FlexCol w="full" wrap="nowrap" borderPlacement="all" alignItems="center" justifyContent="center">
        <CloudinaryPhoto
          fileKey={fileKey}
          mediaStorageStatus={mediaStorageStatus.code}
          thumbnail={thumbnail}
          transformation={transformation}
          type="sm"
          alt={media?.name}
        />
      </FlexCol>
    );
  }

  return null;
};

/** Main Component */
/** this is used to render the list of media from the artist media library
 * in the following locations:
 * - talent & reps: profile edit => select primary photo, video reel, audio reel
 * - talent & reps: profile edit => select profile media
 * - talent & reps: attach media to skill
 * - talent & reps: attach media to profile media
 * - casting director: session group details => attach media to session group
 */
const MediaBin: FC<MediaBinProps> = ({
  'data-qa-id': qaId,
  mediaTypes = [],
  medias: allMedias,
  selectedMediaIds = [],
  isSingleSelection = false,
  onChange,
  maxSelections,
  selectAllHandler,
}) => {
  const { t } = useTranslation();
  const translatedStrings = useMemo(() => mediaStrings(t), [t]);
  const selectNewDisabled = typeof maxSelections === 'number' && selectedMediaIds.length >= maxSelections;

  // in theory the parent component should give us a valid list of media,
  // but we do some validation here to make sure:
  // each media has a mediaId
  // each media is not locked
  // each media is not archived
  // each media is of a valid media type for the mediaTypes prop
  const medias = allMedias.filter(
    (m) =>
      m?.mediaId &&
      typeof m?.isLocked === 'boolean' &&
      !m?.isLocked &&
      typeof m?.mediaStatus?.code === 'string' &&
      m?.mediaStatus?.code !== 'ARCHIVED' &&
      mediaTypes?.includes(m?.mediaType?.code as MediaTypeCodeEnum)
  );
  // photos can render a slightly tighter grid
  // so we want to know if we have ONLY photo media
  const photosOnly = useMemo(
    () =>
      medias.reduce((allPhotos, media) => {
        if (
          !allPhotos ||
          media?.mediaType?.code !== MediaTypeCodeEnum.Photo ||
          media?.mediaStatus?.code === 'ARCHIVED'
        ) {
          return false;
        }

        return allPhotos;
      }, true),
    [medias]
  );
  const hasMultipleTypes = mediaTypes.length > 1;

  const qaMediaType = hasMultipleTypes ? 'media' : mediaTypes?.[0]?.toLowerCase();
  const stringsKey = hasMultipleTypes ? 'multiple' : mediaTypes?.[0];
  const noMediaText = translatedStrings.empty[stringsKey];
  const bodyTitle = translatedStrings.title[stringsKey];
  const validSelectedIds = (isSingleSelection ? selectedMediaIds.slice(0, 1) : selectedMediaIds)?.map((id) =>
    id?.toString()
  );

  return (
    <FlexCol wrap="nowrap" w="full" className="cn_atom_px-2" gap="4" data-testid="media-list-selection-wrapper">
      <BodyTextLarge fontWeight="semibold" data-qa-id={`${qaId}-${qaMediaType}-heading`}>
        {bodyTitle}
      </BodyTextLarge>

      {!isSingleSelection && !!selectAllHandler && (
        <Checkbox
          label={t('form.label.selectAll')}
          isChecked={medias?.length === selectedMediaIds?.length}
          onChange={(checked) => selectAllHandler(checked.target.checked)}
        />
      )}
      <GridDynamic
        w="full"
        columns="1fr"
        autoRows="1fr"
        gap="4"
        smProps={{ columns: photosOnly ? '1fr 1fr' : '1fr' }}
        mdProps={{ columns: photosOnly ? '1fr 1fr 1fr' : '1fr 1fr' }}
        lgProps={{ columns: photosOnly ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr' }}
      >
        {medias?.length > 0 &&
          medias.map((media) => {
            const { mediaId, mediaType } = media;
            const mediaQaKey = mediaType?.code?.toLowerCase();
            const mediaLabel = translatedStrings.label[mediaType?.code];
            const isSelected = validSelectedIds.includes(mediaId?.toString());

            return (
              <FlexCol
                wrap="nowrap"
                w="full"
                gap="2"
                justifyContent="space-between"
                key={media.mediaId}
                data-testid={`media-in-bin-${media.mediaId}`}
              >
                <MediaRenderer media={media} />
                {isSingleSelection ? (
                  <RadioInput
                    data-qa-id={`${qaId}-${mediaQaKey}-radio-button`}
                    name="radio1"
                    label={mediaLabel}
                    id={`media-radio-${mediaId}`}
                    checked={isSelected}
                    value={mediaId?.toString()}
                    onChange={() => onChange?.(media)}
                    size="lg"
                  />
                ) : (
                  <Checkbox
                    data-qa-id={`${qaId}-${mediaQaKey}-checkbox`}
                    label={mediaLabel}
                    isChecked={isSelected}
                    onChange={() => onChange?.(media)}
                    disabled={selectNewDisabled && !isSelected}
                  />
                )}
              </FlexCol>
            );
          })}
      </GridDynamic>

      {medias?.length === 0 && (
        <FlexRow justifyContent="center" w="full">
          <H2Text data-qa-id={`${qaId}-no-${qaMediaType}`}>{noMediaText}</H2Text>
        </FlexRow>
      )}
    </FlexCol>
  );
};

export default MediaBin;
